import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, watch } from "vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import ViewDateTime from "@/components/base/ViewDateTime.vue";
import { storeToRefs } from "pinia";
import { useEUAccountsStore } from '@/store_v3/EUAccountsStore';
import BaseButton from "@/components-UI/BaseButton.vue";
import SvgDownload from "@/assets/icons/download-uploaded.svg";
import SvgTrash from "@/assets/icons/trash.svg";
import BaseDialog from "@/components-UI/modals/BaseDialog.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import BaseInput from "@/components-UI/BaseInput.vue";
import BaseCombobox from "@/components-UI/BaseCombobox.vue";
import BaseCheckbox from "@/components-UI/BaseCheckbox.vue";
import { downloadFiles, sendEUAccountEmail } from "@/api/revenue";
import { downloadFileToDisk } from "@/api/index";
import { showNotification, showServerError } from "@/utils";
import type { IContractor, IEUAccountFile } from "@/api/types/revenue";

/** Данные формы отправки счёта рекламодателю. */
type SendInvoiceToAdvertiserForm = {
    /** Имена отправляемых файлов для заголовка модалки */
    fileNames: string[];
    /** Идентификаторы отправляемых файлов: уходят в send-email */
    fileIds: number[];
    recipient: string;
    cc: string[];
    subject: string;
    /** Период счёта для темы письма, формат yyyy-MM */
    periodYm: string;
    /** Контрагент найден на фронте: только тогда пустая «Копия» значит, что её очистили */
    isContractorFound: boolean;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ComponentFiles',
  setup(__props) {

const EUAccountsStore = useEUAccountsStore();
const {
    isLoading,
    form,
    selectedAdvertiser,
    EUAccountObj,
    invoiceFiles,
} = storeToRefs(EUAccountsStore);

const isFilesModal = ref(false);
const isSendToAdvertiserModal = ref(false);
const isSending = ref(false);
const selectedFileIds = ref<number[]>([]);

const sendInvoiceToAdvertiserForm = reactive<SendInvoiceToAdvertiserForm>({
    fileNames: [],
    fileIds: [],
    recipient: '',
    cc: [],
    subject: '',
    periodYm: '',
    isContractorFound: false,
});

const currentContractor = computed<IContractor | null>(() => {
    const id = form.value.contractor_id;
    const list = selectedAdvertiser.value?.contractors;
    if (id == null || !list?.length) {
        return null;
    }
    return list.find((c) => c.id === id) ?? null;
});

const invoicePeriodYm = computed(() => {
    const raw = form.value.period || EUAccountObj.value?.period || '';
    if (!raw) {
        return '';
    }
    const s = String(raw).trim();
    if (s.length >= 7 && s[4] === '-') {
        return s.slice(0, 7);
    }
    return s;
});

const normalizeAdditionalEmails = (raw: string[] | undefined): string[] => {
    if (!raw?.length) {
        return [];
    }
    return raw.map((e) => String(e).trim()).filter(Boolean);
};

const buildDefaultInvoiceEmailSubject = (emailLanguage: string | undefined, periodYm: string): string => {
    const lang = (emailLanguage || 'ru').toLowerCase();
    const p = periodYm || 'yyyy-mm';
    switch (lang) {
    case 'en':
        return `An invoice from Leadgid has been issued to you for ${p}.`;
    case 'es':
        return `Se le ha emitido una factura de Leadgid correspondiente a ${p}.`;
    case 'ru':
    default:
        return `Вам сформирован счёт Leadgid за ${p}.`;
    }
};

const openSendToAdvertiserModal = (files: IEUAccountFile[]): void => {
    const contractor = currentContractor.value;
    const periodYm = invoicePeriodYm.value;

    sendInvoiceToAdvertiserForm.fileNames = files.map(getFileName);
    sendInvoiceToAdvertiserForm.fileIds = files.map((file) => file.id);
    sendInvoiceToAdvertiserForm.recipient = contractor?.invoicing_email ?? '';
    sendInvoiceToAdvertiserForm.cc = [...normalizeAdditionalEmails(contractor?.invoicing_email_additionals)];
    sendInvoiceToAdvertiserForm.periodYm = periodYm;
    sendInvoiceToAdvertiserForm.isContractorFound = contractor !== null;
    sendInvoiceToAdvertiserForm.subject = buildDefaultInvoiceEmailSubject(
        selectedAdvertiser.value?.email_language,
        periodYm,
    );

    isSendToAdvertiserModal.value = true;
};

const clearSendCcEmails = (): void => {
    sendInvoiceToAdvertiserForm.cc = [];
};

const onSendToAdvertiserApprove = async (): Promise<void> => {
    const invoiceId = EUAccountObj.value?.id;
    if (!invoiceId || isSending.value || !sendInvoiceToAdvertiserForm.fileIds.length) {
        return;
    }

    const recipient = sendInvoiceToAdvertiserForm.recipient.trim();
    const subject = sendInvoiceToAdvertiserForm.subject.trim();
    const cc = normalizeAdditionalEmails(sendInvoiceToAdvertiserForm.cc);
    const fileIds = sendInvoiceToAdvertiserForm.fileIds;

    try {
        isSending.value = true;
        // Пустые получателя и тему не отправляем: бэк подставит их сам
        await sendEUAccountEmail(invoiceId, {
            ...(recipient ? { recipient } : {}),
            // Без поля бэк возьмёт копию из контрагента, а `cc: []` значит «без копии».
            // Пустое поле без найденного контрагента не очищали — копию оставляем бэку
            ...(cc.length || sendInvoiceToAdvertiserForm.isContractorFound ? { cc } : {}),
            ...(subject ? { subject } : {}),
            // Без file_ids бэк приложит все файлы счёта, поэтому список всегда непустой
            file_ids: fileIds,
        });
        showNotification('Письмо отправлено рекламодателю', 'success');
        isSendToAdvertiserModal.value = false;
        isFilesModal.value = false;
        selectedFileIds.value = [];
    } catch (err) {
        // Модалку не закрываем, чтобы не потерять введённые получателя, копию и тему
        showServerError(err, 'Не удалось отправить письмо рекламодателю');
    } finally {
        isSending.value = false;
    }
};

/** Файлы от свежего к старому: в карточке показываем только самый последний. */
const sortedFiles = computed<IEUAccountFile[]>(() => {
    return [...invoiceFiles.value].sort((a, b) => {
        const aTime = new Date(a.created_at ?? '').getTime();
        const bTime = new Date(b.created_at ?? '').getTime();
        return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
    });
});

const selectedFiles = computed<IEUAccountFile[]>(() => {
    return sortedFiles.value.filter((file) => selectedFileIds.value.includes(file.id));
});

const toggleFileSelection = (id: number, isChecked: boolean): void => {
    selectedFileIds.value = isChecked
        ? [...selectedFileIds.value, id]
        : selectedFileIds.value.filter((selectedId) => selectedId !== id);
};

const latestFile = computed<IEUAccountFile | null>(() => sortedFiles.value[0] ?? null);

/** Имя файла для показа и для сохранения на диск */
const getFileName = (file: IEUAccountFile | null): string => {
    if (!file) {
        return '';
    }
    const base = file.original_name || file.name || `Файл №${file.id}`;
    // Расширение уже может быть в имени — второй раз не приклеиваем
    if (!file.extension || base.toLowerCase().endsWith(`.${file.extension.toLowerCase()}`)) {
        return base;
    }
    return `${base}.${file.extension}`;
};

const downloadingFileId = ref<number | null>(null);

const onDownloadFile = async (file: IEUAccountFile): Promise<void> => {
    if (!file?.id || downloadingFileId.value !== null) {
        return;
    }

    try {
        downloadingFileId.value = file.id;
        const blob = await downloadFiles(file.id);
        downloadFileToDisk(blob, getFileName(file));
    } catch (err) {
        showServerError(err, 'Не удалось скачать файл');
    } finally {
        downloadingFileId.value = null;
    }
};

// Файлы счёта приходят отдельным запросом, поэтому ждём появления id счёта
watch(() => EUAccountObj.value?.id, (id) => {
    if (id) {
        EUAccountsStore.getInvoiceFiles(id);
    }
}, { immediate: true });

return { __sfc: true,EUAccountsStore, isLoading, form, selectedAdvertiser, EUAccountObj, invoiceFiles, isFilesModal, isSendToAdvertiserModal, isSending, selectedFileIds, sendInvoiceToAdvertiserForm, currentContractor, invoicePeriodYm, normalizeAdditionalEmails, buildDefaultInvoiceEmailSubject, openSendToAdvertiserModal, clearSendCcEmails, onSendToAdvertiserApprove, sortedFiles, selectedFiles, toggleFileSelection, latestFile, getFileName, downloadingFileId, onDownloadFile, BaseEditableCard, ViewDateTime, BaseButton, SvgDownload, SvgTrash, BaseDialog, BaseList, BaseListItem, BaseInput, BaseCombobox, BaseCheckbox }
}

})